import { AfterViewInit, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { InputDialogData, InputDialogResult, RoomTypeOption } from './input-dialog.types';
import { XButtonComponent } from '../../x-kit/components/x-button/x-button.component';
import { SearchFeatureComponent } from '../../search/components/search-feature/search-feature.component';
import { XSelectComponent } from '../../x-kit/components/x-select/x-select.component';
import { TMenuItem } from '../../x-kit/components/x-menu/x-menu.component';
import { finalize, take } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-input-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    FormsModule,
    XButtonComponent,
    SearchFeatureComponent,
    XSelectComponent,
    TranslatePipe,
  ],
  templateUrl: './input-dialog.component.html',
  styleUrls: ['./input-dialog.component.less'],
})
export class InputDialogComponent implements AfterViewInit {
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  inputValue: string = '';
  roomTypeOptions: RoomTypeOption[] = [];
  roomTypeMenuOptions: TMenuItem[] = [];
  selectedRoomType: string | null = null;
  enableValuationSearch: boolean = false;
  selectedSearchResult: any = null;
  get defaultValuationLinkText(): string {
    return this.translate.instant('explore_new_plans');
  }
  isSubmitting = false;
  submitError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<InputDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InputDialogData,
    private router: Router,
    private translate: TranslateService,
  ) {
    this.inputValue = data.initialValue || '';
    this.roomTypeOptions = data.roomTypes || [];
    this.roomTypeMenuOptions = this.roomTypeOptions.map((option) => ({
      id: option.value,
      text: option.label,
    }));
    this.enableValuationSearch = !!data.enableValuationSearch;
    this.selectedRoomType = this.resolveDefaultRoomType();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.inputElement?.nativeElement) {
        this.inputElement.nativeElement.focus();
      }
    }, 0);
  }

  onSearchResultSelected(result: any) {
    this.selectedSearchResult = result;
  }

  onPrimaryClick(): void {
    console.log('[InputDialog] onPrimaryClick called', {
      isSubmitting: this.isSubmitting,
      isPrimaryDisabled: this.isPrimaryDisabled,
      inputValue: this.inputValue,
      hasConfirmAction: !!this.data.confirmAction
    });
    
    if (this.isSubmitting) {
      console.log('[InputDialog] Blocked: isSubmitting');
      return;
    }
    if (this.isPrimaryDisabled) {
      console.log('[InputDialog] Blocked: isPrimaryDisabled', {
        isValuationPlotRequired: this.isValuationPlotRequired,
        selectedPlotUuid: this.selectedPlotUuid
      });
      return;
    }

    const result: InputDialogResult = {
      confirmed: true,
      value: this.inputValue,
      buttonValue: this.data.primaryButton?.value,
      roomType: this.selectedRoomType || undefined,
      searchResult: this.selectedSearchResult
    };

    console.log('[InputDialog] Closing dialog with result:', result);
    const action$ = this.data.confirmAction?.(result);
    if (!action$) {
      this.dialogRef.close(result);
      return;
    }

    this.isSubmitting = true;
    this.submitError = null;
    this.dialogRef.disableClose = true;

    action$
      .pipe(
        take(1),
        finalize(() => {
          this.isSubmitting = false;
          this.dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: (actionResult) => {
          this.dialogRef.close({ ...result, actionResult });
        },
        error: (err: any) => {
          const message = typeof err?.message === 'string' ? err.message : this.translate.instant('failed_to_create_case');
          this.submitError = message;
        },
      });
  }

  onSecondaryClick(): void {
    if (this.isSubmitting) {
      return;
    }
    const result: InputDialogResult = {
      confirmed: false,
      value: this.inputValue,
      buttonValue: this.data.secondaryButton?.value,
      roomType: this.selectedRoomType || undefined,
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    if (this.isSubmitting) {
      return;
    }
    const result: InputDialogResult = {
      confirmed: false,
      roomType: this.selectedRoomType || undefined,
    };
    this.dialogRef.close(result);
  }

  get shouldShowRoomTypeSelect(): boolean {
    // Only show room type select if roomTypeLabel is provided (visible to user)
    return this.roomTypeOptions.length > 0 && !!this.data.roomTypeLabel;
  }

  get shouldShowValuationSearch(): boolean {
    // Show valuation search if:
    // 1. enableValuationSearch is true AND
    // 2. selectedRoomType is 'valuation'
    // Note: valuationBlockMessage doesn't block search - it's shown separately
    return (
      this.enableValuationSearch &&
      this.selectedRoomType === 'valuation'
    );
  }

  onRoomTypeChange(value: string): void {
    this.selectedRoomType = value;
    this.submitError = null;
  }

  get selectedPlotUuid(): string | null {
    const uuid = this.selectedSearchResult?.data?.uuid ?? this.selectedSearchResult?.uuid;
    return typeof uuid === 'string' && uuid.length ? uuid : null;
  }

  get isValuationPlotRequired(): boolean {
    return this.shouldShowValuationSearch && this.selectedRoomType === 'valuation';
  }

  get isPrimaryDisabled(): boolean {
    if (this.isSubmitting) return true;
    
    // Block primary button if valuationBlockMessage is present (user is not PRO)
    if (this.data.valuationBlockMessage) {
      return true;
    }

    // Only require plot UUID if valuation search is actually shown and required
    if (this.isValuationPlotRequired) {
      return !this.selectedPlotUuid;
    }
    return false;
  }

  onValuationBlockLinkClick(event: Event): void {
    event.preventDefault();

    // If there's a link href, navigate to it and close dialog
    if (this.data.valuationBlockLinkHref) {
      this.dialogRef.close({ confirmed: false, valuationBlockLinkClicked: true });
      this.router.navigateByUrl(this.data.valuationBlockLinkHref);
    } else {
      // Fallback to old behavior
    this.dialogRef.close({ confirmed: false, valuationBlockLinkClicked: true });
    }
  }

  get valuationBlockLinkText(): string {
    return this.data.valuationBlockLinkText || this.defaultValuationLinkText;
  }

  get hasValuationBlockLink(): boolean {
    return !!(this.data.valuationBlockLinkText || this.data.valuationBlockLinkHref);
  }

  get valuationBlockMessageParts(): { before: string; after: string; hasInlineLink: boolean } {
    const message = this.data.valuationBlockMessage || '';
    const linkText = this.valuationBlockLinkText;

    if (!message || !linkText) {
      return { before: message, after: '', hasInlineLink: false };
    }

    const idx = message.indexOf(linkText);
    if (idx < 0) {
      return { before: message, after: '', hasInlineLink: false };
    }

    return {
      before: message.slice(0, idx),
      after: message.slice(idx + linkText.length),
      hasInlineLink: true,
    };
  }

  private resolveDefaultRoomType(): string | null {
    if (!this.roomTypeOptions.length) {
      return null;
    }

    if (this.data.defaultRoomType && this.roomTypeOptions.some((option) => option.value === this.data.defaultRoomType)) {
      return this.data.defaultRoomType;
    }

    return this.roomTypeOptions[0].value;
  }
}

